import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Package,
  CheckCircle,
  Loader2,
  X,
  ShoppingCart,
  Edit3,
  AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Order, OrderItem, PackageDimensions } from '../types';
import { formatCurrency } from '../lib/utils';
import { fetchProductById } from '../services/productService';
import { autoSelectBoxForOrder } from '../services/shippingService';

interface PackOrderModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  onPackOrder?: (orderId: string) => Promise<void>;
}

interface PackItem extends OrderItem {
  packed: boolean;
  packedQuantity: number;
  listingQuantity: number;
  totalQuantity: number;
}

export function PackOrderModal({ order, isOpen, onClose, onPackOrder }: PackOrderModalProps) {
  const [packItems, setPackItems] = useState<PackItem[]>([]);
  const [isPacking, setIsPacking] = useState(false);
  const [packageBox, setPackageBox] = useState<PackageDimensions | null>(order?.shipping?.packageDimensions || null);
  const [calculatingBox, setCalculatingBox] = useState(false);

  React.useEffect(() => {
    if (order?.shipping?.packageDimensions) {
      setPackageBox(order.shipping.packageDimensions);
    } else {
      setPackageBox(null);
    }
  }, [order]);

  // Initialize pack items when modal opens
  React.useEffect(() => {
    const initializePackItems = async () => {
      if (isOpen && order) {
        const initialPackItems: PackItem[] = await Promise.all(
          order.items.map(async (item) => {
            let listingQuantity = item.listingQuantity;
            
            // If listingQuantity is missing, fetch it from the product
            if (!listingQuantity) {
              try {
                const product = await fetchProductById(item.id);
                listingQuantity = product?.listingQuantity || 1;
              } catch (error) {
                console.error('Failed to fetch product listing quantity:', error);
                listingQuantity = 1; // Fallback to 1
              }
            }
            
            const totalQuantity = listingQuantity * item.quantity;
            
            return {
              ...item,
              packed: false,
              packedQuantity: totalQuantity,
              listingQuantity,
              totalQuantity
            };
          })
        );
        setPackItems(initialPackItems);
      }
    };

    initializePackItems();
  }, [isOpen, order]);

  const handlePackedToggle = (itemId: string, packed: boolean) => {
    setPackItems(items => 
      items.map(item => 
        item.id === itemId 
          ? { ...item, packed, packedQuantity: packed ? item.totalQuantity : 0 }
          : item
      )
    );
  };

  const handleQuantityChange = (itemId: string, quantity: number) => {
    setPackItems(items => 
      items.map(item => 
        item.id === itemId 
          ? { 
              ...item, 
              packedQuantity: Math.max(0, Math.min(quantity, item.totalQuantity)),
              packed: quantity > 0
            }
          : item
      )
    );
  };

  const handlePackOrder = async () => {
    if (!order || !onPackOrder) {
      toast.error('Unable to pack order');
      return;
    }

    const unpackedItems = packItems.filter(item => !item.packed || item.packedQuantity === 0);
    if (unpackedItems.length > 0) {
      toast.error('Please pack all items before confirming');
      return;
    }

    try {
      setIsPacking(true);
      await onPackOrder(order.id);
      toast.success('Order packed successfully');
      onClose();
    } catch (error) {
      console.error('Failed to pack order:', error);
      toast.error('Failed to pack order. Please try again.');
    } finally {
      setIsPacking(false);
    }
  };

  const getOrderNumber = () => {
    if (!order) return '';
    return order.orderNumber || order.id;
  };

  const allItemsPacked = packItems.every(item => item.packed && item.packedQuantity > 0);
  const packedItemsCount = packItems.filter(item => item.packed && item.packedQuantity > 0).length;

  if (!order) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-emerald-600 to-teal-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Package className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-xl font-bold text-white"
                    >
                      Pack Order
                    </motion.h2>
                    <motion.div 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="flex items-center gap-4 text-white/80 text-sm mt-1"
                    >
                      <span>Order: {getOrderNumber()}</span>
                      <span>Customer: {order.customer.name}</span>
                      <span>Total: {formatCurrency(order.total)}</span>
                    </motion.div>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content */}
            <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
              <div className="p-6 space-y-6">
                {packageBox ? (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl p-4 border border-emerald-100 shadow-sm flex items-center justify-between gap-3"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">Recommended Box</p>
                      <p className="text-xs text-gray-500">Saved from shipping rate calculation</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-gray-800 text-right">
                      <div className="font-semibold">{packageBox.name || 'Box'}</div>
                      <div className="text-xs text-gray-600">
                        {(packageBox.length ?? '?')} × {(packageBox.width ?? '?')} × {(packageBox.height ?? '?')} {packageBox.unit || 'in'}
                        {packageBox.weight ? ` • ${packageBox.weight} oz` : ''}
                      </div>
                      </div>
                      <Button
                        onClick={async () => {
                          if (!order) return;
                          setCalculatingBox(true);
                          try {
                            const result = await autoSelectBoxForOrder(order.id);
                            setPackageBox(result);
                            toast.success('Box dimensions recalculated and saved');
                          } catch (err: any) {
                            console.error('Failed to recalculate box', err);
                            toast.error(err?.message || 'Failed to recalculate box');
                          } finally {
                            setCalculatingBox(false);
                          }
                        }}
                        disabled={calculatingBox || !order}
                        variant="outline"
                        size="sm"
                        className="whitespace-nowrap"
                      >
                        {calculatingBox ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Recalculating...
                          </>
                        ) : (
                          'Recalculate'
                        )}
                      </Button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl p-4 border border-dashed border-emerald-200 shadow-sm flex items-center justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">No box selected</p>
                      <p className="text-xs text-gray-500">Auto-calculate the best-fit box for this order.</p>
                    </div>
                    <Button
                      onClick={async () => {
                        if (!order) return;
                        setCalculatingBox(true);
                        try {
                          const result = await autoSelectBoxForOrder(order.id);
                          setPackageBox(result);
                          toast.success('Box dimensions saved to order');
                        } catch (err: any) {
                          console.error('Failed to auto-select box', err);
                          toast.error(err?.message || 'Failed to auto-select box');
                        } finally {
                          setCalculatingBox(false);
                        }
                      }}
                      disabled={calculatingBox || !order}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      {calculatingBox ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculating...
                        </>
                      ) : (
                        'Calculate Box'
                      )}
                    </Button>
                  </motion.div>
                )}

                {/* Packing Progress */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-900">Packing Progress</h3>
                    <div className="flex items-center gap-2">
                      <ShoppingCart className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">
                        {packedItemsCount} of {packItems.length} items packed
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${packItems.length > 0 ? (packedItemsCount / packItems.length) * 100 : 0}%` }}
                    ></div>
                  </div>
                </motion.div>

                {/* Order Items */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Items to Pack
                    </h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {packItems.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className={`p-4 hover:bg-gray-50 transition-colors ${
                          item.packed ? 'bg-emerald-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              checked={item.packed}
                              onCheckedChange={(checked) => handlePackedToggle(item.id, checked as boolean)}
                              className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                            />
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                            />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="text-sm font-medium text-gray-900 truncate">
                                {item.name}
                              </h4>
                              <span className="text-sm text-gray-500">
                                {formatCurrency(item.price)} each
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-500">
                              <span>Listing Qty: {item.listingQuantity}</span>
                              <span>Ordered: {item.quantity}</span>
                              <span className="font-medium text-gray-700">Total Qty: {item.totalQuantity}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Pack:</span>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                min="0"
                                max={item.totalQuantity}
                                value={item.packedQuantity}
                                onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 0)}
                                className="w-16 h-8 text-center text-sm"
                              />
                              <span className="text-sm text-gray-500">/ {item.totalQuantity}</span>
                            </div>
                          </div>

                          <div className="flex items-center">
                            {item.packed && item.packedQuantity > 0 ? (
                              <CheckCircle className="h-5 w-5 text-emerald-600" />
                            ) : (
                              <div className="h-5 w-5 rounded-full border-2 border-gray-300" />
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>

                {/* Alert for incomplete packing */}
                {!allItemsPacked && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl"
                  >
                    <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        Please pack all items before confirming
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Check off items as you pack them to track your progress
                      </p>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>

            {/* Enhanced Footer */}
            <div className="bg-gray-50 border-t border-gray-200 p-6 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Package className="h-4 w-4" />
                  <span>
                    {allItemsPacked ? 'All items packed' : `${packedItemsCount} of ${packItems.length} items packed`}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={isPacking}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handlePackOrder}
                    disabled={!allItemsPacked || isPacking}
                    className="px-6 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {isPacking ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Packing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Confirm Pack
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
